import {
    ActionIcon,
    Button,
    Group,
    Menu,
    Modal,
    Paper,
    Stack,
    Text,
    Title,
    Tooltip,
} from '@mantine/core'
import {IconChevronDown, IconEdit, IconPlus, IconTrash} from '@tabler/icons-react'
import {useQueryClient} from '@tanstack/react-query'
import {useState} from 'react'

import {invalidateCustomerSavedLocations} from 'src/customers/saved-locations/invalidate-saved-locations'

import {useEditRights} from './edit-rights-context'
import type {JobDataLayer} from './job-data-layer'
import {locationFromDetail} from './job-details-utils'
import {LocationEditModal} from './location-edit-modal'
import {jobLocationToInput, type ReconcileLocationInput} from './location-input'
import {LocationList} from './location-list'
import {LocationReadContent} from './location-read-content'
import {validateLocation} from './location-validation'
import type {JobDetail, JobLocation} from './types'
import {createEmptyLocation} from './types'

type JobLocationsSectionProps = {
    job: JobDetail
    jobDataLayer: JobDataLayer
    onJobUpdated: (job: JobDetail) => void
}

type EditingLocationId = string | 'new' | null

type ModalMode = 'closed' | 'edit' | 'create-new'

/**
 * Rebuild the stops this save is not editing. The job detail carries no saved-location ids, so these
 * inputs leave `fromSavedLocationId` unset and the API keeps each stop's origin unchanged.
 */
function savedLocationsToInputs(locations: JobDetail['locations']): ReconcileLocationInput[] {
    return locations.map((location) => ({
        id: location.id,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        notes: location.notes,
        rememberAsSavedLocation: false,
    }))
}

function buildSavePayload(
    job: JobDetail,
    editingId: EditingLocationId,
    draft: JobLocation,
): ReconcileLocationInput[] {
    if (editingId === 'new') {
        return [...savedLocationsToInputs(job.locations), jobLocationToInput(draft)]
    }

    return job.locations.map((location) =>
        location.id === editingId
            ? jobLocationToInput({...draft, id: location.id})
            : {
                  id: location.id,
                  name: location.name,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  notes: location.notes,
                  rememberAsSavedLocation: false,
              },
    )
}

export function JobLocationsSection({job, jobDataLayer, onJobUpdated}: JobLocationsSectionProps) {
    const {canEdit} = useEditRights()
    const queryClient = useQueryClient()
    const [modalMode, setModalMode] = useState<ModalMode>('closed')
    const [editingLocationId, setEditingLocationId] = useState<EditingLocationId>(null)
    const [draft, setDraft] = useState<JobLocation>(createEmptyLocation())
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [deleteLocationId, setDeleteLocationId] = useState<string | null>(null)

    function startEditing(location: JobDetail['locations'][number]) {
        setModalMode('edit')
        setEditingLocationId(location.id)
        setDraft(locationFromDetail(location))
        setErrorMessage(null)
    }

    function startAddingLocation() {
        setModalMode('create-new')
        setEditingLocationId('new')
        setDraft(createEmptyLocation())
        setErrorMessage(null)
    }

    function startSelectingSaved() {
        setModalMode('select-saved')
        setEditingLocationId('new')
        setDraft(createEmptyLocation())
        setErrorMessage(null)
    }

    function handleCloseModal() {
        setModalMode('closed')
        setEditingLocationId(null)
        setDraft(createEmptyLocation())
        setErrorMessage(null)
    }

    async function handleSave(location: JobLocation) {
        const validation = validateLocation(location)
        if (!validation.isValid) {
            setErrorMessage('Fix location errors before saving')
            return
        }

        await persistLocations(buildSavePayload(job, editingLocationId, location))
    }

    async function persistLocations(locations: ReconcileLocationInput[]) {
        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.saveLocations(job.id, locations)
            if (locations.some((location) => location.rememberAsSavedLocation)) {
                invalidateCustomerSavedLocations(queryClient, job.customerId)
            }
            onJobUpdated(updatedJob)
        } catch (error) {
            throw error instanceof Error ? error : new Error('Failed to save locations')
        } finally {
            setSaving(false)
        }
    }

    function handleDeleteClick(locationId: string) {
        setDeleteLocationId(locationId)
    }

    function handleCancelDelete() {
        setDeleteLocationId(null)
    }

    async function handleConfirmDelete() {
        if (deleteLocationId == null) return

        try {
            await persistLocations(
                savedLocationsToInputs(job.locations).filter(
                    (location) => location.id !== deleteLocationId,
                ),
            )
            setDeleteLocationId(null)
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save locations')
        }
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Title order={3}>Locations</Title>

                {errorMessage ? (
                    <Text size="sm" c="red">
                        {errorMessage}
                    </Text>
                ) : null}

                <LocationList
                    items={job.locations.map((location) => (
                        <LocationReadContent
                            key={location.id}
                            location={{
                                ...locationFromDetail(location),
                                addressText: location.addressText ?? null,
                            }}
                            source="job_location"
                            nameActions={
                                !canEdit ? null : (
                                    <Group gap="xs">
                                        <ActionIcon
                                            size="sm"
                                            variant="default"
                                            aria-label={`Edit ${location.name}`}
                                            onClick={() => startEditing(location)}
                                            disabled={saving}
                                        >
                                            <IconEdit size={16} />
                                        </ActionIcon>
                                        {job.locations.length > 1 ? (
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                color="red"
                                                aria-label={`Delete ${location.name}`}
                                                onClick={() => handleDeleteClick(location.id)}
                                                disabled={saving}
                                            >
                                                <IconTrash size={16} />
                                            </ActionIcon>
                                        ) : (
                                            <Tooltip label="The last location cannot be deleted">
                                                <ActionIcon
                                                    size="sm"
                                                    variant="subtle"
                                                    color="red"
                                                    aria-label={`Delete ${location.name}`}
                                                    disabled
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Group>
                                )
                            }
                        />
                    ))}
                    emptyState={
                        job.locations.length === 0 ? (
                            <Text size="sm" c="dimmed">
                                No locations yet. Add one to mark job sites and stops.
                            </Text>
                        ) : null
                    }
                    footer={
                        !canEdit ? null : (
                            <Menu position="bottom-start" width="target">
                                <Menu.Target>
                                    <Button
                                        variant="light"
                                        leftSection={<IconPlus size={16} />}
                                        rightSection={<IconChevronDown size={16} />}
                                        disabled={saving}
                                    >
                                        Add location
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Item onClick={startAddingLocation}>
                                        Create new location
                                    </Menu.Item>
                                    <Menu.Item onClick={startSelectingSaved}>
                                        Select from saved
                                    </Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        )
                    }
                />

                <LocationEditModal
                    isOpen={modalMode !== 'closed'}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    mode={modalMode === 'closed' ? 'create-new' : modalMode}
                    source="job_location_editor"
                    initialLocation={
                        modalMode === 'edit' && editingLocationId !== 'new' ? draft : undefined
                    }
                    customerId={job.customerId}
                />

                <Modal
                    opened={deleteLocationId !== null}
                    onClose={handleCancelDelete}
                    title="Remove location"
                    centered
                >
                    <Stack gap="md">
                        <Text>
                            Are you sure you want to remove this location? This action cannot be
                            undone.
                        </Text>
                        <Group justify="flex-end">
                            <Button variant="subtle" onClick={handleCancelDelete}>
                                Cancel
                            </Button>
                            <Button color="red" onClick={handleConfirmDelete}>
                                Remove
                            </Button>
                        </Group>
                    </Stack>
                </Modal>
            </Stack>
        </Paper>
    )
}
